import { useEffect, useState } from "react";
import { BellRing, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { webPushSupported, enableWebPush } from "@/lib/webPush";

const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
const SNOOZE_KEY = "mcf.push.snoozeAt";

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent || "");
}
function isStandalone() {
  return (
    (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches) ||
    (typeof window !== "undefined" && window.navigator?.standalone === true)
  );
}

// Banner invito attivazione Push. Riusa interamente enableWebPush()/webPushSupported()
// del sistema Web Push esistente: non introduce un secondo sistema di notifiche.
export function PushBanner() {
  const [mode, setMode] = useState(null); // 'activate' | 'ios-install' | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozed && Date.now() - snoozed < SNOOZE_MS) return;
    if (webPushSupported()) {
      if (Notification.permission === "default") setMode("activate");
    } else if (isIOS() && !isStandalone()) {
      setMode("ios-install");
    }
  }, []);

  if (!mode) return null;

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    setMode(null);
  };

  const activate = async () => {
    setBusy(true);
    const res = await enableWebPush(true).catch(() => ({ ok: false, reason: "error" }));
    setBusy(false);
    if (res.ok) {
      toast.success("Notifiche attivate");
      setMode(null);
      return;
    }
    if (res.reason === "denied") {
      toast("Permesso negato. Puoi riattivarlo dalle impostazioni del browser (icona del lucchetto nella barra degli indirizzi).");
    } else {
      toast("Non è stato possibile attivare le notifiche in questo momento.");
    }
    setMode(null);
  };

  return (
    <div
      data-testid="push-banner"
      className="relative mb-4 flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
        {mode === "ios-install" ? <Smartphone className="h-5 w-5" /> : <BellRing className="h-5 w-5" />}
      </div>
      <div className="min-w-0 flex-1">
        {mode === "activate" ? (
          <>
            <p className="text-[15px] font-extrabold text-foreground">Attiva le notifiche</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Ricevi subito avvisi su viaggi, missioni, messaggi e aggiornamenti importanti.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid="push-banner-enable"
                onClick={activate}
                disabled={busy}
                className="rounded-full bg-primary px-4 py-2 text-sm font-extrabold text-primary-foreground disabled:opacity-60"
              >
                {busy ? "Attivazione…" : "Attiva notifiche"}
              </button>
              <button
                data-testid="push-banner-later"
                onClick={snooze}
                className="rounded-full bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground"
              >
                Non ora
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-[15px] font-extrabold text-foreground">Notifiche su iPhone</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Per ricevere le notifiche su iPhone, aggiungi MyCargoFlow alla schermata Home.
            </p>
            <div className="mt-3">
              <button
                data-testid="push-banner-later"
                onClick={snooze}
                className="rounded-full bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground"
              >
                Ho capito
              </button>
            </div>
          </>
        )}
      </div>
      <button
        data-testid="push-banner-dismiss"
        onClick={snooze}
        aria-label="Chiudi"
        className="shrink-0 rounded-lg p-1 text-muted-foreground hover:bg-secondary"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
